import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Toolbar,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, Refresh, Edit } from "@mui/icons-material";

import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";

import {
  createTermDeposits,
  fetchTermDeposits,
} from "../../redux/masters/termDepositSlice";
import { getOptions, mergeOptions } from "../../redux/masters/optionSlice";
import { OptionsStatus } from "../../constants/enum";
import { convertTermDepositsToOptionData } from "../../utils/optionUtils";
import IssueLoanModalForm from "../../components/modals/AddIssueLoanModal";

const IssueLoan = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showSuccess, showError } = useToast();

  const {
    items: termDeposits,
    loading,
    error,
  } = useSelector((state) => state.termDeposits);

  const {
    data: depositOptions = {},
    loading: optionLoading,
    error: optionError,
  } = useSelector((state) => state.option || {});

  const optionKey = OptionsStatus.DepositAccounts;
  const optionData = depositOptions[optionKey] || [];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editingDepositAccountId, setEditingDepositAccountId] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawerClose = () => setMobileOpen(false);
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditData(null);
  };

  useEffect(() => {
    dispatch(fetchTermDeposits());
  }, [dispatch]);

  const ensureDepositAccountsOptionLoaded = async () => {
    if (!optionData || optionData.length === 0) {
      try {
        await dispatch(getOptions([optionKey])).unwrap();
        await dispatch(fetchTermDeposits()).unwrap();
        const termDepositOptions = convertTermDepositsToOptionData(termDeposits);
        dispatch(mergeOptions(termDepositOptions));
      } catch (err) {
        showError("Failed to load options");
        throw err;
      }
    } else {
      const termDeposits = await dispatch(fetchTermDeposits()).unwrap();
      dispatch(mergeOptions(convertTermDepositsToOptionData(termDeposits)));
    }
  };

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      await ensureDepositAccountsOptionLoaded();
      setEditData(null);
      setOpenModal(true);
    } catch (err) {
      console.log("Error:", err);
      showError("Failed to load loan options");
    } finally {
      setAddLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await dispatch(fetchTermDeposits()).unwrap();
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError(error.message || "Failed to refresh data");
    }
  };

  const handleLoanSubmit = async (formData) => {
    try {
      // You can replace this with API call later
      console.log("Loan issued:", formData);
      await dispatch(createTermDeposits(formData)).unwrap();
      await dispatch(fetchTermDeposits()).unwrap();
      showSuccess(
        editData
          ? "Loan details updated successfully"
          : "Loan issued successfully"
      );
      handleCloseModal();
    } catch (error) {
      showError(error.message || "Failed to issue loan");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Header & Sidebar */}
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/loan-deposit/issue-loan"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        {/* 🟢 Issue Loan Modal */}
        <IssueLoanModalForm
          open={openModal}
          onClose={handleCloseModal}
          onSubmit={handleLoanSubmit}
          initialData={editData}
        />

        <Toolbar sx={{ minHeight: 64 }} />
        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          {/* Header */}
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Issue Loan
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                {/* Refresh (mobile) */}
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>

                {/* Add New Button */}
                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {/* Refresh (desktop) */}
                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />

          {/* Table */}
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <TableSkeleton rows={5} columns={13} />
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <EnhancedTable
                data={termDeposits || []}
                columns={[
                  {
                    id: "depositCode",
                    label: "Code",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "depositName",
                    label: "Name",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "depositType",
                    label: "Deposit Type",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "depositFrequency",
                    label: "Frequency",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "interestRate",
                    label: "ROI (%)",
                    sortable: true,
                    render: (value) => `${value}%`,
                    hidden: isMobile,
                  },
                  {
                    id: "interestFrequency",
                    label: "Int. Credit",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "interestType",
                    label: "Int Type",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "active",
                    label: "Status",
                    sortable: true,
                    render: (value) =>
                      value ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip
                          label="Inactive"
                          size="small"
                          variant="outlined"
                        />
                      ),
                  },
                  {
                    id: "editable",
                    label: "*",
                    sortable: false,
                    render: (_, row) => (
                      <Tooltip
                        title={
                          row.editable
                            ? "Edit Loan Details"
                            : "Data not editable"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              !row.editable ||
                              editingDepositAccountId !== null
                            }
                            onClick={async (e) => {
                              e.stopPropagation();
                              setEditingDepositAccountId(row.id);
                              try {
                                await ensureDepositAccountsOptionLoaded();
                                setEditData(row);
                                setOpenModal(true);
                              } catch (err) {
                                showError("Failed to load loan data");
                              } finally {
                                setEditingDepositAccountId(null);
                              }
                            }}
                          >
                            {editingDepositAccountId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Edit fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                ]}
                title="Term Deposits"
                loading={loading}
                emptyMessage="No loans found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default IssueLoan;
